/** Canonical HirePort shell palette — core colors plus dedicated borderColor. */
export const hireportColors = {
  colorPrimary: '#34CE72',
  colorSecondary: '#17884A',
  colorText: '#0F172A',
  colorThird: '#33485a',
  colorSubtle: '#475569',
  /** Tertiary text (slate-500) — quiet but still readable on white (4.7:1). */
  colorMuted: '#64748b',
  /** Faintest text (slate-400) — placeholders and disabled states only. */
  colorFaint: '#94a3b8',
  colorBrightness: '#F8FAFC',
  /** Divider/card hairlines (slate-200). */
  borderColor: '#e2e8f0',
  /** Control outlines — inputs/selects need a step darker (slate-300) to read as fields. */
  controlBorderColor: '#cbd5e1',
  colorItemSelectedBg: '#eef9f1',
  /* Blue informative colors */
  colorInfo: '#0958d9',
  colorInfoBg: '#e6f4ff',
  /** Amber/orange caution palette (warning pills, alerts, caution text). */
  colorWarning: '#854D0E',
  colorWarningHover: '#713F0C',
  colorWarningActive: '#5C3409',
  colorWarningBg: '#fffbe6',
  colorWarningBorder: '#ffe58f',
  /** Red error/destructive palette (disconnect, delete, validation errors). */
  colorError: '#CF1322',
  colorErrorHover: '#A8071A',
  colorErrorActive: '#820014',
  colorErrorBg: '#FFF1F0',
  colorErrorBgFilledHover: '#CF1322',
  colorErrorBorder: '#FFA39E',
} as const;

/**
 * Dark-scheme counterpart to `hireportColors`. Surfaces are slate (not antd's
 * pure black). `colorSecondary` is lightened so links/success stay readable.
 * Status backgrounds are low-alpha tints of their base color.
 */
export const hireportDarkColors = {
  colorPrimary: '#34CE72',
  colorSecondary: '#5FDD94',
  colorText: '#F8FAFC',
  colorThird: '#475569',
  colorSubtle: '#CBD5E1',
  /** Tertiary text on slate-800 (~5.3:1). */
  colorMuted: '#94A3B8',
  /** Placeholders / disabled on slate-800. */
  colorFaint: '#64748B',
  /** Page canvas (slate-950). */
  colorBrightness: '#0F172A',
  borderColor: '#334155',
  controlBorderColor: '#475569',
  colorItemSelectedBg: '#2d3c54',
  colorInfo: '#69B1FF',
  colorInfoBg: 'rgba(105, 177, 255, 0.16)',
  colorWarning: '#FBBF24',
  colorWarningHover: '#FCD34D',
  colorWarningActive: '#F59E0B',
  colorWarningBg: 'rgba(251, 191, 36, 0.16)',
  colorWarningBorder: 'rgba(251, 191, 36, 0.35)',
  colorError: '#F87171',
  colorErrorHover: '#FCA5A5',
  colorErrorActive: '#EF4444',
  colorErrorBg: 'rgba(248, 113, 113, 0.16)',
  colorErrorBgFilledHover: '#F87171',
  colorErrorBorder: 'rgba(248, 113, 113, 0.4)',
} as const;

/** Card / elevated surfaces for the dark scheme (slate-800 / slate-700). */
export const hireportDarkSurfaces = {
  colorBgContainer: '#1E293B',
  colorBgElevated: '#334155',
} as const;

export const hireportLightSurfaces = {
  colorBgContainer: '#ffffff',
  colorBgElevated: '#ffffff',
} as const;

function parseHexRgb(hex: string): [number, number, number] | null {
  const match = hex.trim().match(/^#([0-9a-fA-F]{6})$/);
  if (!match) return null;
  const h = match[1];
  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ];
}

export function rgbaFromHex(hex: string, alpha: number): string {
  const rgb = parseHexRgb(hex);
  if (!rgb) return hex;
  const [r, g, b] = rgb;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Composite `foreground` at `foregroundAlpha` onto `background` as opaque #rrggbb. */
export function mixHex(
  foreground: string,
  background: string,
  foregroundAlpha: number,
): string {
  const fg = parseHexRgb(foreground);
  const bg = parseHexRgb(background);
  if (!fg || !bg) return foreground;
  const mix = (f: number, b: number) =>
    Math.round(f * foregroundAlpha + b * (1 - foregroundAlpha));
  const toHex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${toHex(mix(fg[0], bg[0]))}${toHex(mix(fg[1], bg[1]))}${toHex(mix(fg[2], bg[2]))}`;
}
